<script>
import feather from 'feather-icons';
import { socialLinks } from '../../data/socialLinks';

export default {
	name: 'AppBanner',
	data: () => ({
		theme: '',
		socials: socialLinks,
		stats: [
			{ value: '6+', label: 'Years' },
			{ value: '70+', label: 'Projects' },
			{ value: '200k+', label: 'Users served' },
		],
		techStack: [
			{ name: 'Laravel', color: 'rose' },
			{ name: 'Vue.js', color: 'emerald' },
			{ name: 'n8n', color: 'amber' },
			{ name: 'Zapier', color: 'sky' },
			{ name: 'Make', color: 'indigo' },
			{ name: 'OpenAI', color: 'slate' },
			{ name: 'AI Agents', color: 'blue' },
		],
		services: [
			{ title: 'SaaS Backends', detail: 'Laravel, Vue, payments, APIs', icon: 'server' },
			{ title: 'AI Automation', detail: 'n8n, Zapier, Make workflows', icon: 'zap' },
			{ title: 'AI Agents', detail: 'OpenAI, RAG, MCP, chatbots', icon: 'cpu' },
		],
		techColors: {
			rose: 'bg-rose-50 border-rose-200 text-rose-600 hover:border-rose-300 dark:bg-rose-500/10 dark:border-rose-500/25 dark:text-rose-300 dark:hover:border-rose-500/50',
			emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600 hover:border-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/25 dark:text-emerald-300 dark:hover:border-emerald-500/50',
			sky: 'bg-sky-50 border-sky-200 text-sky-600 hover:border-sky-300 dark:bg-sky-500/10 dark:border-sky-500/25 dark:text-sky-300 dark:hover:border-sky-500/50',
			slate: 'bg-slate-100 border-slate-200 text-slate-600 hover:border-slate-300 dark:bg-slate-400/10 dark:border-slate-400/25 dark:text-slate-300 dark:hover:border-slate-400/50',
			blue: 'bg-blue-50 border-blue-200 text-blue-600 hover:border-blue-300 dark:bg-blue-500/10 dark:border-blue-500/25 dark:text-blue-300 dark:hover:border-blue-500/50',
			indigo: 'bg-indigo-50 border-indigo-200 text-indigo-600 hover:border-indigo-300 dark:bg-indigo-500/10 dark:border-indigo-500/25 dark:text-indigo-300 dark:hover:border-indigo-500/50',
			amber: 'bg-amber-50 border-amber-200 text-amber-600 hover:border-amber-300 dark:bg-amber-500/10 dark:border-amber-500/25 dark:text-amber-300 dark:hover:border-amber-500/50',
		},
		dotColors: {
			rose: 'bg-rose-500',
			emerald: 'bg-emerald-500',
			sky: 'bg-sky-500',
			slate: 'bg-slate-400',
			blue: 'bg-blue-500',
			indigo: 'bg-indigo-500',
			amber: 'bg-amber-500',
		},
	}),
	created() {
		this.theme = localStorage.getItem('theme') || 'dark';
	},
	mounted() {
		feather.replace();
		this.theme = localStorage.getItem('theme') || 'dark';
	},
	updated() {
		feather.replace();
	},
};
</script>

<template>
	<section class="relative flex flex-col items-center justify-center py-16 sm:py-24 overflow-hidden">
		<div
			class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent pointer-events-none" />

		<!-- Available badge -->
		<div class="mb-8 flex items-center gap-2 px-4 py-2 rounded-lg border border-green-500/30 bg-green-500/10 text-green-500 dark:text-green-400 text-sm font-general-medium shadow-lg shadow-green-500/5"
			data-aos="fade-down" data-aos-delay="100">
			<span class="w-2 h-2 rounded-full bg-green-400 animate-pulse-slow inline-block" />
			Available for work
		</div>

		<!-- Avatar -->
		<div class="mb-8 relative" data-aos="zoom-in" data-aos-delay="200">
			<div class="p-[3px] rounded-full bg-gradient-to-br from-amber-400 to-indigo-300">
				<img src="@/assets/images/Arham/front.webp" alt="Arham" loading="lazy"
					class="w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover block" />
			</div>
		</div>

		<!-- Greeting -->
		<p class="font-general-medium text-base sm:text-lg text-gray-500 dark:text-slate-400 mb-2" data-aos="fade-up"
			data-aos-delay="300">
			Hi, I'm
		</p>

		<!-- Name & Title -->
		<h1 class="font-general-bold text-4xl sm:text-6xl xl:text-7xl text-center font-bold mb-4 leading-tight max-w-5xl"
			data-aos="fade-up" data-aos-delay="400">
			<span class="text-gradient">Arham</span>
			<span class="text-gray-900 dark:text-white"> Natiq </span>
		</h1>

		<h2 class="font-general-semibold text-xl sm:text-2xl xl:text-3xl text-center text-slate-500 dark:text-slate-400 mb-6"
			data-aos="fade-up" data-aos-delay="500">
			Full-Stack Engineer & AI Automation Expert
		</h2>

		<!-- Bio -->
		<p class="font-general-regular text-base sm:text-lg text-center text-gray-600 dark:text-slate-400 max-w-3xl leading-relaxed mb-10"
			data-aos="fade-up" data-aos-delay="600">
			I help SaaS founders build, automate, and scale products with
			<span class="text-amber-500 dark:text-amber-400 font-general-medium">PHP / Laravel</span>,
			<span class="text-indigo-500 dark:text-indigo-400 font-general-medium">Vue.js / React</span>,
			<span class="text-amber-400 dark:text-amber-300 font-general-medium">n8n / Zapier / Make</span> &amp;
			<span class="text-indigo-400 dark:text-indigo-300 font-general-medium">OpenAI agents</span>, with 6+ years
			of experience.
		</p>

		<!-- CTA Buttons -->
		<div class="flex flex-col sm:flex-row gap-4 items-center" data-aos="fade-up" data-aos-delay="700">
			<router-link to="/projects"
				class="btn-gradient px-7 py-3 rounded-lg text-white font-general-medium text-sm sm:text-base shadow-lg">
				View My Work
			</router-link>
			<router-link to="/contact"
				class="px-7 py-3 rounded-lg font-general-medium text-sm sm:text-base transition-all duration-300 border"
				:class="theme === 'dark'
					? 'text-slate-300 border-white/15 hover:border-amber-500/50 hover:bg-amber-500/10'
					: 'text-gray-700 border-gray-300 hover:border-amber-400 hover:bg-amber-50'">
				Get in Touch
			</router-link>
		</div>

		<!-- Social links -->
		<div class="mt-8 flex items-center gap-3" data-aos="fade-up" data-aos-delay="750">
			<a v-for="social in socials" :key="social.id" :href="social.url" target="_blank" rel="noopener noreferrer"
				:aria-label="social.name"
				class="w-10 h-10 flex items-center justify-center rounded-lg border transition-all duration-300" :class="theme === 'dark'
					? 'border-white/10 bg-white/[0.03] text-slate-400 hover:border-amber-500/50 hover:text-amber-400 hover:bg-amber-500/10'
					: 'border-gray-200 bg-white text-gray-500 hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50'">
				<i :data-feather="social.icon" class="w-4 h-4" />
			</a>
		</div>

		<!-- Stat strip -->
		<div class="mt-12 grid grid-cols-3 gap-3 sm:gap-5 w-full max-w-2xl" data-aos="fade-up" data-aos-delay="800">
			<template v-for="stat in stats" :key="stat.label">
				<div class="surface-card px-4 py-5 flex flex-col items-center text-center">
					<span class="font-general-bold text-2xl sm:text-3xl font-bold text-gradient">{{ stat.value }}</span>
					<span
						class="font-general-medium text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mt-1">
						{{ stat.label }}
					</span>
				</div>
			</template>
		</div>

		<!-- Tech stack pills -->
		<div class="mt-14 flex flex-wrap justify-center gap-2.5" data-aos="fade-up" data-aos-delay="900">
			<span v-for="tech in techStack" :key="tech.name"
				class="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs rounded-lg font-general-medium border transition-all duration-200 hover:-translate-y-0.5"
				:class="techColors[tech.color]">
				<span class="w-1.5 h-1.5 rounded-full" :class="dotColors[tech.color]" />
				{{ tech.name }}
			</span>
		</div>

		<div class="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-4xl" data-aos="fade-up"
			data-aos-delay="950">
			<div v-for="service in services" :key="service.title" class="surface-card surface-card-hover p-5">
				<div
					class="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
					<i :data-feather="service.icon" class="w-4 h-4" />
				</div>
				<h3 class="font-general-semibold text-gray-900 dark:text-white text-base mb-1">
					{{ service.title }}
				</h3>
				<p class="font-general-regular text-sm text-gray-500 dark:text-slate-400">
					{{ service.detail }}
				</p>
			</div>
		</div>

		<!-- Scroll cue -->
		<div class="mt-16 flex flex-col items-center gap-2 text-gray-400 dark:text-slate-600" data-aos="fade-up"
			data-aos-delay="1000">
			<span class="font-general-medium text-[10px] uppercase tracking-[0.2em]">Scroll</span>
			<span class="w-5 h-8 rounded-full border-2 border-current flex justify-center pt-1.5">
				<span class="w-1 h-1.5 rounded-full bg-current animate-bounce" />
			</span>
		</div>
	</section>
</template>
